import React, { useContext, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import AttributeBooleanInput from "../../components/attributes/AttributeBooleanInput";
import AttributeMultipleSelectInput from "../../components/attributes/AttributeMultipleSelectInput";
import AttributeTextFieldInput from "../../components/attributes/AttributeTextFieldInput";
import AttributeUniqueSelectInput from "../../components/attributes/AttributeUniqueSelectInput";
import { AttributeType, EDGE_DATE_FORMAT, ERROR } from '../../services/common/Constants';
import { hasAttributeListItems } from "../../services/common/AttributeUtils";
import {
  addDossierAttribute,
  addThirdpartyAttribute,
  getAccountAttributeByLabelAndBondType,
} from '../../services/edge/AttributesService';
import { Context } from "../../states/Store";
import useStyles from "../../style/js-style/containers/attribute-values/AttributeValueFormStyle";
import AttributeDateInput from "components/attributes/AttributeDateInput";
import {
  getAccountAttributeById,
  updateContextInstanceAttributeValue,
  updateContextInstanceThirdPartyAttributeValue,
} from '../../services/edge/ContexteService';
import moment from 'moment/moment';

const getFormDefaultValues = (attributeValue) => {
  const result = {
    label: attributeValue.label,
  };
  if (attributeValue.type === AttributeType.LIST_MULTIPLE) {
    result.values = attributeValue.values || [];
  } else {
    result.value = attributeValue.values?.length > 0 ? attributeValue.values[0] : null;
  }
  return result;
};

const AttributeValueForm = ({
  formId,
  bondType,
  attributeValue,
  onError = () => {},
  setSelectedAttribute = () => {},
  onValidateSuccess = () => {},
  onCancel = () => {},
  fetchAttributeValuesList = () => {},
  customHandleSubmit = null,
  id,
                            }) => {
  const [state, dispatch] = useContext(Context);
  const [attributeListItems, setAttributeListItems] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { classes } = useStyles();

  const formMethods = useForm({
    mode: "onChange",
    delayError: 500,
    defaultValues: getFormDefaultValues(attributeValue),
  });

  const handleSuccess = (data) => {
    setIsLoading(false);
    onValidateSuccess(data, attributeValue);
    onCancel();
  }

  const handleUpdateAttribute = async (data) => {
    if (!isLoading) {
      setIsLoading(true);
      try {
        if (moment.isMoment(data.value)) {
          data.value = moment(data.value).format(EDGE_DATE_FORMAT);
        }
        const attributeToSave = {
          label: attributeValue.label,
          values: data.values || (data.value ? [data.value] : null),
        };
        switch (bondType) {
          case "thirdparty":
            await addThirdpartyAttribute(
              state.account.id,
              id,
              attributeToSave,
              null
            );
            fetchAttributeValuesList();
            break;
          case "dossier":
            await addDossierAttribute(
              state.account.id,
              id,
              attributeToSave,
              null
            );
            fetchAttributeValuesList();
            break;
          case "context":
            if (state.isCreatingContextInstance) {
              return handleSuccess(data);
            } else {
              await updateContextInstanceAttributeValue(
                state.account.id,
                state.contextInstance.id,
                {
                  ...attributeValue,
                  ...attributeToSave
                },
                null
              );
            }
            break;
          case "context_third_party":
            if (state.isAssociatingContextThirdParty) {
              return handleSuccess(data)
            } else {
              await updateContextInstanceThirdPartyAttributeValue(state.account.id,
                state.contextThirdParty.ctxInstanceId,
                state.contextThirdParty.id,
                {
                  ...attributeValue,
                  ...attributeToSave
                },
                null
              );
            }
            break;
          default:
            throw new Error("Wrong bond type");
        }
        handleSuccess(data);
      } catch (error) {
        dispatch({
          type: "ALERT",
          alert: {
            type: ERROR,
            msg: "attributes.errors.updateAttributeValue",
          },
        });
      }
    }
  };

  useEffect(() => {
    async function fetchAttributeByLabel() {
      try {
        let result;
        if (attributeValue?.attributeId !== undefined && attributeValue?.attributeId !== null) {
          result = await getAccountAttributeById(state.account.id, attributeValue.attributeId);
        } else if (attributeValue?.id !== undefined && attributeValue?.id !== null) {
          result = await getAccountAttributeById(state.account.id, attributeValue.id);
        } else {
          await getAccountAttributeByLabelAndBondType(
              state.account.id,
              attributeValue.label,
              bondType,
              null
          );
        }
        const listItems = result.content?.values
          ?.map((x) => x.value)
          ?.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
        setAttributeListItems(listItems);
      } catch (e) {
        dispatch({
          type: "ALERT",
          alert: {
            type: ERROR,
            msg:
              e?.message?.error && e?.message?.error[0].code === "NOT_FOUND"
                ? "ATTRIBUTE_NOT_FOUND"
                : "INTERNAL_ERROR",
          },
        });
      }
    }

    if (hasAttributeListItems(attributeValue.type)) {
      fetchAttributeByLabel();
    } else {
      setAttributeListItems(null);
    }
  }, [state.account, attributeValue, bondType, dispatch]);

  const renderAttributeValueInput = (attributeValue) => {
    switch (attributeValue.type) {
      case AttributeType.LIST_UNIQUE:
        return <AttributeUniqueSelectInput listItems={attributeListItems} setSelectedAttribute={setSelectedAttribute} save={customHandleSubmit? formMethods.handleSubmit(customHandleSubmit) : formMethods.handleSubmit(handleUpdateAttribute)}/>;
      case AttributeType.LIST_MULTIPLE:
        return <AttributeMultipleSelectInput listItems={attributeListItems} setSelectedAttribute={setSelectedAttribute} save={customHandleSubmit? formMethods.handleSubmit(customHandleSubmit) : formMethods.handleSubmit(handleUpdateAttribute)}/>;
      case AttributeType.BOOLEAN:
        return <AttributeBooleanInput setSelectedAttribute={setSelectedAttribute} save={customHandleSubmit? formMethods.handleSubmit(customHandleSubmit) : formMethods.handleSubmit(handleUpdateAttribute)}/>;
      case AttributeType.DATE:
        return <AttributeDateInput onError={onError} setSelectedAttribute={setSelectedAttribute} save={customHandleSubmit? formMethods.handleSubmit(customHandleSubmit) : formMethods.handleSubmit(handleUpdateAttribute)}/>;
      default:
        return <AttributeTextFieldInput type={attributeValue.type} setSelectedAttribute={setSelectedAttribute} save={customHandleSubmit? formMethods.handleSubmit(customHandleSubmit) : customHandleSubmit? formMethods.handleSubmit(customHandleSubmit) : formMethods.handleSubmit(handleUpdateAttribute)}/>;
    }
  };

  return (
    <FormProvider {...formMethods}>
      <form
        id={formId}
        onSubmit={customHandleSubmit? formMethods.handleSubmit(customHandleSubmit) : formMethods.handleSubmit(handleUpdateAttribute)}
        className={classes.formContainer}
      >
          <div style={{ width: "auto",backgroundColor: "white", borderRadius: "0 12px 12px 0", border: "none !important"}}>
            {renderAttributeValueInput(attributeValue)}
          </div>
      </form>
    </FormProvider>
  );
};

export default AttributeValueForm;
